import { z } from 'zod';
import { normalizePhoneToE164 } from './phone.ts';

/**
 * Who is emailed about a voicemail: owners and managers, the employee or
 * team the number rang, or both (migration 0116). A company default a
 * location or a number may override (`voice-routing.ts`).
 */
export const VOICEMAIL_NOTIFY_OPTIONS = ['office', 'target', 'both'] as const;
export type VoicemailNotify = (typeof VOICEMAIL_NOTIFY_OPTIONS)[number];

/** Outside work hours: ring the phones anyway, or straight to voicemail. */
export const AFTER_HOURS_OPTIONS = ['ring', 'voicemail'] as const;
export type AfterHoursPolicy = (typeof AFTER_HOURS_OPTIONS)[number];

export const CALL_STATUSES = [
  'queued',
  'initiated',
  'ringing',
  'in-progress',
  'completed',
  'busy',
  'failed',
  'no-answer',
  'canceled',
] as const;
export type CallStatus = (typeof CALL_STATUSES)[number];

/**
 * What `voice_calls.from_number` holds for a caller who withheld their number.
 *
 * Twilio reports a blocked or unavailable caller ID either as a word or as one
 * of a few reserved pseudo-numbers. They all mean "nobody we can match", and
 * every such call files on one unresolved thread rather than being refused.
 */
export const ANONYMOUS_CALLER = 'anonymous';

/** Twilio's stand-ins for a withheld caller ID (Anonymous, Restricted, Unknown, Blocked). */
const WITHHELD_CALLER_IDS = new Set([
  '+266696687',
  '+86282452253',
  '+8656696',
  '+2562533',
  '+7378742833',
  'anonymous',
  'restricted',
  'unknown',
  'unavailable',
  'blocked',
  'private',
]);

const e164 = z.string().regex(/^\+[1-9][0-9]{6,14}$/);
const accountSid = z.string().regex(/^AC[0-9a-fA-F]{32}$/);
const callSid = z.string().regex(/^CA[0-9a-fA-F]{32}$/);

/**
 * A caller: a real E.164 number, or `ANONYMOUS_CALLER` for anything else. A
 * withheld caller ID, a short code, or a number Twilio could not normalize is
 * still a signed call from somebody: it is forwarded and can leave a
 * voicemail, matches no customer, and files on the unresolved thread.
 */
export const callerSchema = z.string().transform((value) => {
  const trimmed = value.trim();
  if (WITHHELD_CALLER_IDS.has(trimmed.toLowerCase()) || !/^\+[1-9][0-9]{6,14}$/.test(trimmed)) return ANONYMOUS_CALLER;
  return trimmed;
});

/**
 * Status callbacks: metadata only. Recording parameters are accepted solely by
 * the recording endpoint (`recordingCallbackSchema`) and ignored here.
 */
export const callStatusCallbackSchema = z
  .object({
    AccountSid: accountSid,
    CallSid: callSid,
    From: callerSchema,
    To: e164,
    Direction: z.literal('inbound'),
    CallStatus: z.enum(CALL_STATUSES),
    SequenceNumber: z
      .string()
      .regex(/^\d{1,9}$/)
      .transform(Number),
    CallDuration: z
      .string()
      .regex(/^\d{1,6}$/)
      .transform(Number)
      .optional(),
  })
  .superRefine((value, ctx) => {
    if (isTerminalCallStatus(value.CallStatus) && value.CallDuration === undefined) {
      ctx.addIssue({ code: 'custom', path: ['CallDuration'], message: 'A final call event needs its duration.' });
    }
  });

export type CallStatusCallback = z.infer<typeof callStatusCallbackSchema>;

export function isTerminalCallStatus(status: string): boolean {
  return ['completed', 'busy', 'failed', 'no-answer', 'canceled'].includes(status);
}

/**
 * Twilio's requests about a call the office placed (click-to-call): the leg
 * to the employee's phone (`outbound-api`, From the company line, To that
 * phone) and the leg to the customer it dials once they press 1
 * (`outbound-dial`, From the company line, To the customer, naming the first
 * as `ParentCallSid`). The same shape for the instructions (`/bridge`, with
 * `Digits` from the confirm prompt) and for the status callbacks.
 */
export const outboundCallEventSchema = z.object({
  AccountSid: accountSid,
  CallSid: callSid,
  ParentCallSid: callSid.optional(),
  From: e164,
  To: e164,
  Direction: z.enum(['outbound-api', 'outbound-dial']),
  CallStatus: z.enum(CALL_STATUSES),
  CallDuration: z
    .string()
    .regex(/^\d{1,6}$/)
    .transform(Number)
    .optional(),
  Digits: z.string().max(16).optional(),
});
export type OutboundCallEvent = z.infer<typeof outboundCallEventSchema>;

/**
 * Starting a call to a customer from the company's number. `ringPhone` is the
 * phone rung first, in any format a person types (read in the company's
 * country, as customer and user phones are, by services/outbound-calls.ts);
 * omitted, the employee's own phone (or the company's forwarding phone) is rung.
 */
export const startCustomerCallSchema = z
  .object({
    ringPhone: z
      .string()
      .trim()
      .max(32)
      .optional()
      .transform((value) => value || undefined),
  })
  .strict();
export type StartCustomerCallInput = z.infer<typeof startCustomerCallSchema>;

/** What the call panel shows before a call starts (`GET /customers/:id/call`). */
export interface CustomerCallOptions {
  /**
   * Why the office cannot call from the company's number here, or null: calls
   * are off on this deployment or plan, no number takes calls, or the
   * customer has no phone number.
   */
  unavailableReason: 'unavailable' | 'no_line' | 'no_phone' | null;
  /** The company number the customer sees. */
  line: string | null;
  /** The customer's number, as dialed. */
  customerPhone: string | null;
  /** The phone rung first unless another is entered: the employee's own, else the company's forwarding phone. */
  ringPhone: string | null;
  /** The customer's clock: the location's timezone, else the company's. */
  timezone: string;
  /** Whether it is between 9 PM and 8 AM there. A warning, not a refusal (Chris, 19 Oct 2026). */
  quietHours: boolean;
}

/** A call the office started: its place on the thread, and the phone ringing now. */
export interface StartedCustomerCall {
  callId: string;
  messageId: string;
  conversationId: string;
  status: CallStatus;
  ringPhone: string;
  line: string;
}

/** Twilio's request for instructions when a call arrives (`/incoming`). */
export const incomingCallSchema = z.object({
  AccountSid: accountSid,
  CallSid: callSid,
  From: callerSchema,
  To: e164,
  Direction: z.literal('inbound'),
  CallStatus: z.enum(CALL_STATUSES),
});
export type IncomingCall = z.infer<typeof incomingCallSchema>;

export const DIAL_CALL_STATUSES = ['completed', 'answered', 'busy', 'no-answer', 'failed', 'canceled'] as const;

/** What the forwarded leg did (`/dial-result`, the `<Dial action>`). */
export const dialResultSchema = incomingCallSchema.extend({
  // Absent when there was nothing to dial (no forwarding number saved).
  DialCallStatus: z.enum(DIAL_CALL_STATUSES).optional(),
  /**
   * Whether the caller was connected to a phone that picked up. A screened
   * team leg that picked up and pressed nothing (a voicemail greeting) ends
   * `completed` but never bridged: the caller still goes to voicemail.
   */
  DialBridged: z.enum(['true', 'false']).optional(),
});
export type DialResult = z.infer<typeof dialResultSchema>;

/** The caller pressed a menu digit (`/ivr`, the `<Gather action>`): the call's own parameters plus the digit. */
export const menuChoiceSchema = incomingCallSchema.extend({
  Digits: z.string().max(16).optional(),
});
export type MenuChoice = z.infer<typeof menuChoiceSchema>;

/**
 * Twilio asking what an employee's phone hears on a team leg (`/screen`,
 * the `<Number url>`): the leg's own SID, the inbound call it belongs to
 * (`ParentCallSid`), and on the second request the digit they pressed.
 */
export const screenLegSchema = z.object({
  AccountSid: accountSid,
  CallSid: callSid,
  ParentCallSid: callSid,
  Digits: z.string().max(16).optional(),
});
export type ScreenLeg = z.infer<typeof screenLegSchema>;

export const RECORDING_STATUSES = ['completed', 'absent', 'failed'] as const;

/**
 * Twilio's `recordingStatusCallback`. Accepted ONLY on the recording endpoint;
 * the status callback keeps ignoring recording parameters.
 */
export const recordingCallbackSchema = z.object({
  AccountSid: accountSid,
  CallSid: callSid,
  RecordingSid: z.string().regex(/^RE[0-9a-fA-F]{32}$/),
  RecordingStatus: z.enum(RECORDING_STATUSES),
  RecordingDuration: z
    .string()
    .regex(/^-?\d{1,6}$/)
    .transform((value) => Math.max(0, Number(value)))
    .optional(),
  RecordingSource: z.string().max(32).optional(),
});
export type RecordingCallback = z.infer<typeof recordingCallbackSchema>;

/** Voicemails shorter than this are kept as a call, never transcribed or sent to AI. */
export const VOICEMAIL_MIN_SECONDS = 2;
/** The longest message a caller can leave, in seconds (`<Record maxLength>`). */
export const VOICEMAIL_MAX_SECONDS = 180;

export const RING_TIMEOUT_MIN_SECONDS = 15;
export const RING_TIMEOUT_MAX_SECONDS = 40;
export const RING_TIMEOUT_DEFAULT_SECONDS = 20;
export const VOICE_GREETING_MAX_CHARS = 500;
/** An uploaded greeting: short, and in a format Twilio's `<Play>` accepts. */
export const VOICE_GREETING_MAX_BYTES = 5 * 1024 * 1024;
export const VOICE_GREETING_TYPES = ['audio/mpeg', 'audio/wav', 'audio/x-wav'] as const;

export const GREETING_KINDS = ['tts', 'audio'] as const;
export type GreetingKind = (typeof GREETING_KINDS)[number];

/**
 * What an owner saves in Settings. `forwardTo` is any phone format a person
 * types; it is stored as E.164 or refused. An `audio` greeting needs a stored
 * recording, which the upload endpoint provides; the save checks that.
 */
export const voiceSettingsSchema = z
  .object({
    forwardTo: z
      .string()
      .trim()
      .max(32)
      .nullable()
      .transform((value, ctx) => {
        if (!value) return null;
        const normalized = normalizePhoneToE164(value);
        if (!normalized) {
          ctx.addIssue({ code: 'custom', message: 'Enter a phone number that can take calls.' });
          return z.NEVER;
        }
        return normalized;
      }),
    ringTimeoutSeconds: z.number().int().min(RING_TIMEOUT_MIN_SECONDS).max(RING_TIMEOUT_MAX_SECONDS),
    greetingKind: z.enum(GREETING_KINDS),
    greetingText: z
      .string()
      .trim()
      .max(VOICE_GREETING_MAX_CHARS)
      .nullable()
      .transform((value) => value || null),
    recordAnswered: z.boolean(),
    transcribe: z.boolean(),
    /**
     * Company defaults a location or a number may override (migration 0116).
     * Optional: a mobile build from before the Numbers screen omits them, and
     * they then stay as they are.
     */
    voicemailNotify: z.enum(VOICEMAIL_NOTIFY_OPTIONS).optional(),
    afterHours: z.enum(AFTER_HOURS_OPTIONS).optional(),
  })
  .strict();
export type VoiceSettingsInput = z.infer<typeof voiceSettingsSchema>;

/**
 * Why a texting-number change could not be applied to calls (a refused
 * rebind): the new number cannot receive calls, is not on the Twilio account
 * any more, is the phone calls are forwarded to, belongs to another company,
 * or Twilio could not be reached or updated; or no number can take calls at
 * all (`no_line`: the company's own account is switched off and it has no
 * number BlastoClean issued), or its calls go to a SIP trunk or TwiML App
 * set up in Twilio (`routed_elsewhere`), which would ignore our voice URL.
 */
export const CALL_LINE_PROBLEMS = [
  'cannot_take_calls',
  'not_on_account',
  'forwards_to_itself',
  'held_elsewhere',
  'twilio_unreachable',
  'setup_failed',
  'no_line',
  'routed_elsewhere',
] as const;
export type CallLineProblemReason = (typeof CALL_LINE_PROBLEMS)[number];

export interface VoiceSettings {
  forwardTo: string | null;
  ringTimeoutSeconds: number;
  greetingKind: GreetingKind;
  greetingText: string | null;
  /** Whether an uploaded greeting recording is on file. */
  hasGreetingAudio: boolean;
  recordAnswered: boolean;
  transcribe: boolean;
  /** Who is emailed about a voicemail, unless a location or number says otherwise. */
  voicemailNotify: VoicemailNotify;
  /** Outside work hours: ring anyway, or straight to voicemail; a location or number may override. */
  afterHours: AfterHoursPolicy;
  /** The number taking the company's calls now (its enabled binding), or null when none does. */
  callNumber: string | null;
  /**
   * While no number takes calls: the one saving these settings would set up
   * (the texting number), or null when there is none.
   */
  pendingCallNumber: string | null;
  /**
   * Recordings still on a Twilio account the company disconnected or replaced,
   * which BlastoClean can no longer delete: the owner deletes them in Twilio.
   */
  recordingsLeftOnTwilio: number;
  /**
   * Set when a texting-number change could not be applied to calls, cleared
   * by the next successful bind. `callNumber` is then still the line that
   * answers.
   */
  callLineProblem: { reason: CallLineProblemReason; at: string } | null;
  /**
   * Where a company on its own Twilio account points the number's voice
   * webhooks. Null for a number BlastoClean issued, which is set up for them.
   */
  webhooks: { incomingUrl: string; statusUrl: string } | null;
}

export const TRANSCRIPT_STATUSES = ['none', 'pending', 'done', 'failed'] as const;
export type TranscriptStatus = (typeof TRANSCRIPT_STATUSES)[number];
export const CALL_RECORDING_STATUSES = ['none', 'pending', 'stored', 'failed'] as const;
export type CallRecordingStatus = (typeof CALL_RECORDING_STATUSES)[number];

export interface InboxCall {
  id: string;
  /**
   * Who called whom: a customer calling the company, or the office calling a
   * customer from the company's number (click-to-call). `from` and `to` are
   * as Twilio reported them, so an outbound call is from the company's line.
   */
  direction: 'inbound' | 'outbound';
  from: string;
  to: string;
  status: CallStatus;
  durationSeconds: number | null;
  /** The caller left a message (as opposed to an answered call that was recorded). */
  voicemail?: boolean;
  recording?: {
    status: CallRecordingStatus;
    durationSeconds: number | null;
    /**
     * A short-lived signed path to stream the audio, or null when it cannot
     * be played: not stored yet, or the plan no longer includes calls.
     */
    url: string | null;
    /** True when the audio is kept but hidden until the company upgrades again. */
    locked: boolean;
  } | null;
  transcript?: {
    status: TranscriptStatus;
    text: string | null;
    language: string | null;
    /** Below the length worth transcribing. */
    tooShort: boolean;
  } | null;
  /** The suggested text reply prepared for a voicemail, for a person to edit. */
  aiReplyDraft?: string | null;
  /**
   * Whether Retry would be accepted now: audio that failed (or stalled), or a
   * voicemail transcript that failed, was skipped, or stalled. Computed by the
   * server with the same rule the Retry endpoint applies.
   */
  retryable?: boolean;
}

/**
 * Whether an inbound message is words a reply, a draft or a suggestion can
 * answer: an email, a text, or a voicemail whose transcript is ready.
 */
export function isWrittenInbound(channel: string, voicemailTranscript?: string | null): boolean {
  return channel === 'email' || channel === 'sms' || (channel === 'call' && voicemailTranscript === 'done');
}

/**
 * Why an uploaded greeting cannot be played to callers, or null when it can:
 * a declared type Twilio's `<Play>` accepts, a size under the cap, and bytes
 * that really are that type (an MP3 frame or ID3 tag, a RIFF/WAVE header).
 */
export function greetingAudioProblem(
  bytes: Uint8Array,
  contentType: string,
): 'empty' | 'too_large' | 'type' | 'mismatch' | null {
  if (bytes.byteLength === 0) return 'empty';
  if (bytes.byteLength > VOICE_GREETING_MAX_BYTES) return 'too_large';
  const type = contentType.split(';')[0]?.trim().toLowerCase() ?? '';
  if (!(VOICE_GREETING_TYPES as readonly string[]).includes(type)) return 'type';
  const ascii = (offset: number, length: number) => String.fromCharCode(...bytes.subarray(offset, offset + length));
  if (type === 'audio/mpeg') {
    const id3 = ascii(0, 3) === 'ID3';
    const frame = bytes.length > 1 && bytes[0] === 0xff && ((bytes[1] ?? 0) & 0xe0) === 0xe0;
    return id3 || frame ? null : 'mismatch';
  }
  return bytes.length >= 12 && ascii(0, 4) === 'RIFF' && ascii(8, 4) === 'WAVE' ? null : 'mismatch';
}
